import { validateEmail } from '@/utils/validation'
import { type ChangeEvent, type FormEvent, useState } from 'react'
import { toast } from 'react-toastify'
import useAccessStore from '../../store/accessStore'
import LoginForm from './LoginForm'
import styles from './login.module.css'

type LinkArg = string | { page: string; state?: Record<string, unknown> }

interface LoginProps {
  onLinkClick: (page: LinkArg) => void
  onLoginSuccess: () => void
}

interface LoginValues {
  email: string
  password: string
  [key: string]: string
}

interface LoginErrors {
  email?: string
  password?: string
  general?: string
  [key: string]: string | undefined
}

export const Login = ({ onLinkClick, onLoginSuccess }: LoginProps) => {
  const [values, setValues] = useState<LoginValues>({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState<LoginErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAccessStore()

  const validateForm = (): boolean => {
    const newErrors: LoginErrors = {}
    const emailError = validateEmail(values.email)
    if (emailError) {
      newErrors.email = emailError
      toast.error(emailError)
    }

    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setValues((prev) => ({
      ...prev,
      [id]: value,
    }))

    if (Object.keys(errors).length > 0) {
      setErrors({})
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)
      sessionStorage.setItem('lastLoginEmail', values.email)

      const loginResponse = await login(values.email, values.password)

      if (loginResponse && 'ok' in loginResponse && loginResponse.ok) {
        setTimeout(() => {
          onLoginSuccess()
        }, 100)
      }
    } catch (error: unknown) {
      console.error('Login error:', error)

      const newErrors: LoginErrors = {}

      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { message?: string; data?: Record<string, string> } } }).response
        const backendErrors = response?.data?.data

        // Toasts for backend field errors are raised by the api response interceptor,
        // so here we only mirror them into the inline form errors.
        if (backendErrors) {
          if (backendErrors.email) {
            newErrors.email = backendErrors.email
          }
          if (backendErrors.password) {
            newErrors.password = backendErrors.password
          }
          if (!backendErrors.email && !backendErrors.password) {
            newErrors.general = backendErrors[Object.keys(backendErrors)[0]] || 'Login failed. Please try again.'
          }
        } else {
          newErrors.general = response?.data?.message || 'Login failed. Please try again.'
          if (!response?.data?.message) {
            toast.error(newErrors.general)
          }
        }
      } else {
        newErrors.general = 'Login failed. Please try again.'
        toast.error('Login failed. Please try again.')
      }

      setErrors(newErrors)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.mainContentWrapper}>
      <h1 className={styles.title}>Sign In</h1>
      <p className={styles.textGrey}>Good to see you back!</p>

      <LoginForm
        values={values}
        // errors={errors}
        isLoading={isLoading}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        handleForgotPassword={() => onLinkClick('forgot-password')}
      />

      <div className={styles.additional}>
        <p className={styles.text}>I don't have an account</p>
        <button
          type="button"
          className={styles.link}
          onClick={() => onLinkClick('registration')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onLinkClick('registration')
            }
          }}
        >
          Sign up
        </button>
      </div>
    </div>
  )
}
